// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  PERFECT_SCROLLBAR_MAX_300,
  PERFECT_SCROLLBAR_MAX_340
} from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardEcommerce.json";

import "react-table/react-table.css";

function money(n) {
  return `$${addCommas(Number(n.toFixed ? n.toFixed(2) : n))}`;
}

function EcommerceDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const ordersColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "ecom.col.order" }),
        accessor: "id",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      { Header: formatMessage({ id: "ecom.col.customer" }), accessor: "customer" },
      { Header: formatMessage({ id: "ecom.col.items" }), accessor: "items" },
      {
        Header: formatMessage({ id: "ecom.col.total" }),
        accessor: "total",
        Cell: p => money(p.value)
      },
      {
        Header: formatMessage({ id: "ecom.col.status" }),
        accessor: "status",
        Cell: p => {
          const map = {
            Delivered: "success",
            Shipped: "info",
            Processing: "warning",
            Returned: "secondary",
            Cancelled: "danger"
          };
          return <Badge color={map[p.value] || "light"}>{p.value}</Badge>;
        }
      },
      { Header: formatMessage({ id: "ecom.col.channel" }), accessor: "channel" },
      { Header: formatMessage({ id: "ecom.col.date" }), accessor: "date" }
    ];
    }, [intl]);

  const orderStatusOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.orderStatus.map(o => o.status),
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "68%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const salesTrendOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [0, 3], curve: "smooth" },
      colors: [c.ReactifyChartColor2, c.ReactifyChartColor1],
      plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.salesTrend.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: [
        {
          title: { text: "Orders" },
          labels: { style: { colors: c.primaryColor } }
        },
        {
          opposite: true,
          title: { text: "Revenue ($k)" },
          labels: { style: { colors: c.primaryColor } }
        }
      ],
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light", shared: true }
    };
    }, [colors]);

  const segmentOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: [c.ReactifyChartColor1],
      dataLabels: {
        enabled: true,
        formatter: (val, ctx) => {
          const seg = data.segments[ctx.dataPointIndex];
          return `${seg.revenueShare}% rev`;
        }
      },
      xaxis: {
        categories: data.segments.map(s => s.name),
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const { formatMessage } = intl;
  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--ecommerce">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.ecommerce" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-3">
            <Colxx xxs="12" className="d-flex flex-wrap gap-2">
              <Button color="primary" size="sm">
                <IntlMessages id="ecom.toolbar.new-order" />
              </Button>
              <Button color="outline-secondary" size="sm">
                <IntlMessages id="ecom.toolbar.export" />
              </Button>
              <Button color="outline-secondary" size="sm">
                <IntlMessages id="ecom.toolbar.sync" />
              </Button>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.revenueSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.revenue" />
                  </p>
                  <h4 className="mb-0">{money(k.revenue)}</h4>
                  <span className="text-success small">+{k.revenueDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-shopping-cart text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.orders" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.orders)}</h4>
                  <span className="text-success small">+{k.ordersDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-usd text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.aov" />
                  </p>
                  <h4 className="mb-0">{money(k.aov)}</h4>
                  <span className="text-danger small">{k.aovDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-undo text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.returns" />
                  </p>
                  <h4 className="mb-0">{k.returnsRate}%</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-shopping-basket text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.abandon" />
                  </p>
                  <h4 className="mb-0">{k.cartAbandon}%</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-line-chart text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.cvr" />
                  </p>
                  <h4 className="mb-0">{k.conversionRate}%</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-truck text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.shipping" />
                  </p>
                  <h4 className="mb-0">{k.shippingOnTime}%</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-cubes text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ecom.kpi.skus-active" />
                  </p>
                  <h4 className="mb-0">{data.topSkus.length}+</h4>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.chart.sales-week" />
                  </CardTitle>
                  <Chart
                    options={salesTrendOptions}
                    series={[
                      {
                        name: "Orders",
                        type: "column",
                        data: data.salesTrend.orders
                      },
                      {
                        name: "Revenue ($k)",
                        type: "line",
                        data: data.salesTrend.revenue
                      }
                    ]}
                    type="line"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.chart.order-status" />
                  </CardTitle>
                  <Chart
                    options={orderStatusOptions}
                    series={data.orderStatus.map(o => o.count)}
                    type="donut"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.chart.segments" />
                  </CardTitle>
                  <Chart
                    options={segmentOptions}
                    series={[
                      { data: data.segments.map(s => s.customers) }
                    ]}
                    type="bar"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.section.inventory" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_340}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>{formatMessage({ id: "ecom.col.sku" })}</th>
                          <th>{formatMessage({ id: "ecom.col.product" })}</th>
                          <th>{formatMessage({ id: "ecom.col.on-hand" })}</th>
                          <th>{formatMessage({ id: "ecom.col.reorder" })}</th>
                          <th>{formatMessage({ id: "ecom.col.severity" })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.inventoryAlerts.map((r, i) => (
                          <tr key={i}>
                            <td className="font-weight-medium">{r.sku}</td>
                            <td>{r.name}</td>
                            <td>{r.onHand}</td>
                            <td>{r.reorder}</td>
                            <td>
                              <Badge
                                color={
                                  r.severity === "critical"
                                    ? "danger"
                                    : r.severity === "warning"
                                      ? "warning"
                                      : "success"
                                }
                              >
                                {r.severity}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.section.top-skus" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_300}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>{formatMessage({ id: "ecom.col.product" })}</th>
                          <th>{formatMessage({ id: "ecom.col.sold" })}</th>
                          <th>{formatMessage({ id: "ecom.col.stock" })}</th>
                          <th>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topSkus.map((s, i) => (
                          <tr key={i}>
                            <td className="font-weight-medium">{s.sku}</td>
                            <td>{s.name}</td>
                            <td>{addCommas(s.sold)}</td>
                            <td>{s.stock}</td>
                            <td>{s.margin}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.section.shipments" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_300}>
                    <ul className="list-unstyled mb-0">
                      {data.shipments.map((s, i) => (
                        <li
                          key={i}
                          className="border-bottom pb-3 mb-3 d-flex justify-content-between align-items-start"
                        >
                          <div>
                            <div className="font-weight-medium">{s.id}</div>
                            <div className="text-muted small">
                              {s.carrier} · {s.destination}
                            </div>
                            <Badge color="info" className="mt-1">
                              {s.status}
                            </Badge>
                          </div>
                          <div className="text-right text-muted small">
                            <IntlMessages id="ecom.label.eta" /> {s.eta}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.table.orders" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={6}
                    data={data.orders}
                    columns={ordersColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ecom.section.refunds" />
                  </CardTitle>
                  <ul className="list-unstyled mb-0">
                    {data.refunds.map((r, i) => (
                      <li key={i} className="border-bottom pb-2 mb-2">
                        <div className="d-flex justify-content-between">
                          <span className="font-weight-medium">{r.id}</span>
                          <span>{money(r.amount)}</span>
                        </div>
                        <div className="text-muted small">
                          {r.order} · {r.reason}
                        </div>
                        <div className="text-muted small">{r.date}</div>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default EcommerceDashboard;
